import api from ".";

const bomPath = "/api/bom";
const notFoundCache = new Map();
const NOT_FOUND_TTL_MS = 10000;

export const uploadBomExcel = async (file, token) => {
  if (!file) {
    throw new Error("Excel file is required");
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const headers = {
      "Content-Type": "multipart/form-data",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const { data } = await api.post(`${bomPath}/upload`, formData, {
      headers: {
        ...headers,
      },
    });

    return data;
  } catch (error) {
    console.error("BOM Upload API Error:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });

    throw (
      error.response?.data || {
        success: false,
        message: "BOM upload failed",
      }
    );
  }
};

export const uploadBarcodeRegistryExcel = async (file, token) => {
  if (!file) {
    throw new Error("Excel file is required");
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const headers = {
      "Content-Type": "multipart/form-data",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const { data } = await api.post(
      `${bomPath}/barcode-registry/upload`,
      formData,
      {
        headers: {
          ...headers,
        },
      },
    );

    return data;
  } catch (error) {
    console.error("Barcode registry upload API Error:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });

    throw (
      error.response?.data || {
        success: false,
        message: "Barcode registry upload failed",
      }
    );
  }
};

export const getBomEstimatedCost = async (sku, options = {}) => {
  try {
    const { data } = await api.get(`${bomPath}/by-sku/${sku}`);
    return Number(data.estimatedCost) || 0;
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "BOM cost fetch failed";
    if (typeof options?.onNotFound === "function") {
      const skuKey = String(options?.sku || sku || "").trim().toUpperCase();
      const now = Date.now();
      const lastShown = notFoundCache.get(skuKey) || 0;
      if (!skuKey || now - lastShown > NOT_FOUND_TTL_MS) {
        notFoundCache.set(skuKey || message, now);
        options.onNotFound(message);
      }
    }
    console.error("BOM cost fetch failed:", err);
    return 0;
  }
};

export const getBomBarcodeRegistry = async (params = {}) => {
  try {
    const { data } = await api.get(`${bomPath}/barcode-registry`, { params });
    return data;
  } catch (error) {
    console.error("Get BOM barcode registry failed:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });

    throw (
      error.response?.data || {
        success: false,
        message: "Failed to load BOM barcode registry records",
      }
    );
  }
};

export const updateBarcodeRegistrySku = async ({
  sku,
  upcFull,
  scc14,
} = {}) => {
  try {
    const payload = {
      sku: String(sku || "").trim(),
      upcFull: String(upcFull || "").trim(),
      scc14: String(scc14 || "").trim(),
    };
    const { data } = await api.patch(`${bomPath}/barcode-registry/sku`, payload);
    return data;
  } catch (error) {
    console.error("Update barcode registry SKU failed:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });
    throw (
      error.response?.data || {
        success: false,
        message: "Failed to update barcode registry SKU",
      }
    );
  }
};

const getFilenameFromDisposition = (contentDisposition, fallbackName) => {
  const value = String(contentDisposition || "");
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/["']/g, "");
  }
  const filenameMatch = value.match(/filename="?([^"]+)"?/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1];
  }
  return fallbackName;
};

export const downloadBomExcel = async () => {
  try {
    const res = await api.get(`${bomPath}/export`, {
      responseType: "blob",
    });

    const filename = getFilenameFromDisposition(
      res.headers?.["content-disposition"],
      `bom-database-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );

    return {
      blob: res.data,
      filename,
    };
  } catch (error) {
    console.error("Download BOM Excel API Error:", {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });
    throw error;
  }
};

export const downloadBarcodeRegistryExcel = async () => {
  try {
    const res = await api.get(`${bomPath}/barcode-registry/export`, {
      responseType: "blob",
    });

    const filename = getFilenameFromDisposition(
      res.headers?.["content-disposition"],
      `barcode-registry-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );

    return {
      blob: res.data,
      filename,
    };
  } catch (error) {
    console.error("Download barcode registry Excel API Error:", {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });
    throw error;
  }
};
export const deleteBarcodeRegistryRow = async ({ upcFull } = {}) => {
  try {
    const { data } = await api.delete(`${bomPath}/barcode-registry/row`, {
      data: { upcFull: String(upcFull || "").trim() },
    });
    return data;
  } catch (error) {
    console.error("Delete barcode registry row failed:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });
    throw (
      error.response?.data || {
        success: false,
        message: "Failed to delete barcode registry row",
      }
    );
  }
};
